import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Container,
  Avatar,
  Button,
  alpha,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Router as RouterIcon,
  SettingsEthernet as OnuIcon,
  LocationOn as LocationIcon,
  Cable as CableIcon,
  Map as MapIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
// Componentes das abas
import OltsTab from './ftth/OltsTab';
import OnusRegistradasTab from './ftth/OnusRegistradasTab';
import CaixasTab from './ftth/CaixasTab';
import CircuitosTab from './ftth/CircuitosTab';
import MapNetworkTab from './ftth/MapNetworkTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      style={{ padding: '0px 0px 0px 0px' }}
      hidden={value !== index}
      id={`ftth-tabpanel-${index}`}
      aria-labelledby={`ftth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 0, sm: 0 }, minHeight: '320px' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `ftth-tab-${index}`,
    'aria-controls': `ftth-tabpanel-${index}`,
  };
}

const FtthPage: React.FC = () => {
  const location = useLocation();
  const initialTab = (location.state as { ftthTab?: number } | null)?.ftthTab;
  const [value, setValue] = useState(() => (typeof initialTab === 'number' ? initialTab : 0));
  const [createFormRequest, setCreateFormRequest] = useState<'cto' | 'ceo' | null>(null);
  const [oltCreateTrigger, setOltCreateTrigger] = useState(0);
  const [onuRefreshTrigger, setOnuRefreshTrigger] = useState(0);
  const [circuitoCreateTrigger, setCircuitoCreateTrigger] = useState(0);
  const [caixasSelectedCount, setCaixasSelectedCount] = useState(0);
  const [caixasNotifyTrigger, setCaixasNotifyTrigger] = useState(0);
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (typeof initialTab === 'number') {
      setValue(initialTab);
    }
  }, [initialTab]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(primario, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
      {/* Cabeçalho */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(primario, 0.12), color: primario, width: 40, height: 40 }}>
              <RouterIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Gerenciamento FTTH
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Gerenciamento completo da infraestrutura de fibra óptica
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {value === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOltCreateTrigger((t) => t + 1)}
                size="small"
                fullWidth={isMobile}
                sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
              >
                Adicionar OLT
              </Button>
            )}
            {value === 1 && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setOnuRefreshTrigger((t) => t + 1)}
                size="small"
                fullWidth={isMobile}
                sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
              >
                Atualizar
              </Button>
            )}
            {value === 2 && (
              <Box display="flex" gap={1}>
                {caixasSelectedCount > 0 && (
                  <Badge badgeContent={caixasSelectedCount} color="success">
                    <Button
                      variant="contained"
                      startIcon={<WhatsAppIcon />}
                      color="success"
                      onClick={() => setCaixasNotifyTrigger(t => t + 1)}
                      size="small"
                      fullWidth={isMobile}
                      sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5, whiteSpace: 'nowrap' }}
                    >
                      Notificar Clientes
                    </Button>
                  </Badge>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateFormRequest('cto')}
                  size="small"
                  fullWidth={isMobile}
                  sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
                >
                  Cadastrar CTO
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateFormRequest('ceo')}
                  size="small"
                  fullWidth={isMobile}
                  sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
                >
                  Cadastrar CEO
                </Button>
              </Box>
            )}
            {value === 3 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCircuitoCreateTrigger((t) => t + 1)}
                size="small"
                fullWidth={isMobile}
                sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
              >
                Novo Circuito
              </Button>
            )}
          </Box>
        </Box>
        <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minHeight: 42, gap: 0.8 },
            }}
          >
            <Tab
              label="OLTs"
              icon={<RouterIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              {...a11yProps(0)}
            />
            <Tab
              label="ONUs Registradas"
              icon={<OnuIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              {...a11yProps(1)}
            />
            <Tab
              label="Caixas"
              icon={<LocationIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              {...a11yProps(2)}
            />
            <Tab
              label="Circuitos"
              icon={<CableIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              {...a11yProps(3)}
            />
            <Tab
              label="Mapa da Rede"
              icon={<MapIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              {...a11yProps(4)}
            />
          </Tabs>
        </Paper>
        {/* Conteúdo das abas */}
        <TabPanel value={value} index={0}>
          <OltsTab createTrigger={oltCreateTrigger} />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <OnusRegistradasTab refreshTrigger={onuRefreshTrigger} />
        </TabPanel>

        <TabPanel value={value} index={2}>
          <CaixasTab
            createFormRequest={createFormRequest}
            onCreateFormRequestHandled={() => setCreateFormRequest(null)}
            notifyTrigger={caixasNotifyTrigger}
            onSelectionChange={setCaixasSelectedCount}
          />
        </TabPanel>

        <TabPanel value={value} index={3}>
          <CircuitosTab createTrigger={circuitoCreateTrigger} />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <MapNetworkTab initialViabilidade={(location.state as any)?.viabilidade} />
        </TabPanel>
    </Box>
  );
};

export default FtthPage; 